import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdChevronLeft } from "react-icons/md";
import { IoMdInformationCircleOutline } from "react-icons/io";
import { Tooltip } from "@mui/material";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import type { ScenariaPlan } from "src/model/model";
import type { IMedia } from "src/model/unityObject";
import SearchableDropdown from "src/common/components/SearchableDropdown/SearchableDropdown";
import InfoIconWithTooltip from "src/common/components/Icon/InfoIconWithTooltip";
import ModalStandard from "src/common/components/Modal/ModalStandard";
import { closeStandardModal, openStandardModal } from "src/common/state/slice/modal/modalSlice";
import { setAlert } from "src/features/alert/state/alertsSlice";
import {
  fetchSearchIndex,
  getProjectGroupSize,
  getSearchIndex,
} from "src/features/projectGroups/state/projectGroupsSlice";
import {
  fetchAndUpdateSpecificConversation,
  generateFromPlanThunk,
  getScenarioHelperContextImages,
} from "../../state/chatbotSlice";
import DecorStatusRow from "../referenceImage/DecorStatusRow";
import BlockadeImagesSection from "./BlockadeImagesSection";
import { computeGenerationEstimate } from "./generationEstimate";

const discardPromptOverridesModalId = "discardPromptOverridesModal";
const confirmGenerationModalId = "confirmGenerationModal";

interface ConfigureGenerationPanelProps {
  plan?: ScenariaPlan;
  conversationId: number;
  ragAgentIds: number[];
  lockedDestinationProjectGroupId?: number;
  onBack: () => void;
  onGenerationStarted: () => void;
  isReferenceImageBusy: boolean;
  referenceImageRealigningTo: "image" | "ai" | null;
  onPickReferenceImage: () => void;
  onRemoveReferenceImage: (media: IMedia) => void;
}

type Destination = "new" | "assign";
type AuthType = "none" | "simplified" | "classic";

const ConfigureGenerationPanel = ({
  plan,
  conversationId,
  ragAgentIds,
  lockedDestinationProjectGroupId,
  onBack,
  onGenerationStarted,
  isReferenceImageBusy,
  referenceImageRealigningTo,
  onPickReferenceImage,
  onRemoveReferenceImage,
}: ConfigureGenerationPanelProps) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const searchIndex = useAppSelector(getSearchIndex);
  const contextImages = useAppSelector(getScenarioHelperContextImages);

  const suggestedName = useCallback(
    (dest: Destination) => {
      const prettyGenerationDate = new Intl.DateTimeFormat(i18n.language, {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      }).format(new Date(Date.now() + 2 * 60 * 60 * 1000));
      const base = t(
        dest === "assign"
          ? "pages.chat.defaultGeneratedModuleName"
          : "pages.chat.defaultGeneratedCourseName",
        { date: prettyGenerationDate },
      );

      const takenNames = new Set(
        searchIndex
          .filter((item) => item.type === (dest === "assign" ? "project" : "projectGroup"))
          .map((item) => item.name),
      );
      if (!takenNames.has(base)) return base;
      for (let n = 2; n < 100; n += 1) {
        const candidate = `${base} (${n})`;
        if (candidate.length > 24) break;
        if (!takenNames.has(candidate)) return candidate;
      }
      return base;
    },
    [i18n.language, searchIndex, t],
  );

  const isDestinationLocked = typeof lockedDestinationProjectGroupId === "number";
  const [name, setName] = useState(() => suggestedName(isDestinationLocked ? "assign" : "new"));
  const [isNameEdited, setIsNameEdited] = useState(false);
  const [destination, setDestination] = useState<Destination>(
    isDestinationLocked ? "assign" : "new",
  );
  const [assignedToProjectGroup, setAssignedToProjectGroup] = useState(
    isDestinationLocked ? String(lockedDestinationProjectGroupId) : "",
  );
  const [authType, setAuthType] = useState<AuthType>("none");
  const [generateBlockadeImages, setGenerateBlockadeImages] = useState(false);
  const [promptOverrides, setPromptOverrides] = useState<Record<string, string>>({});
  const [isComputingPgSize, setIsComputingPgSize] = useState(false);
  const [sizeError, setSizeError] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  const planStamp = plan?.lastRefinedAt ?? plan?.generatedAt;
  const [prevPlanStamp, setPrevPlanStamp] = useState(planStamp);
  if (prevPlanStamp !== planStamp) {
    setPrevPlanStamp(planStamp);
    setPromptOverrides({});
  }

  const setPromptOverride = (sceneId: string, text: string) =>
    setPromptOverrides((prev) => ({ ...prev, [sceneId]: text }));
  const clearPromptOverride = (sceneId: string) =>
    setPromptOverrides((prev) => {
      if (!(sceneId in prev)) return prev;
      const next = { ...prev };
      delete next[sceneId];
      return next;
    });
  const hasOverrides = Object.keys(promptOverrides).length > 0;

  const estimate = computeGenerationEstimate(plan, generateBlockadeImages);

  useEffect(() => {
    void dispatch(fetchSearchIndex({}));
  }, [dispatch]);

  useEffect(() => {
    if (!isNameEdited) setName(suggestedName(destination));
  }, [destination, isNameEdited, suggestedName]);

  useEffect(() => {
    if (destination !== "assign" || !assignedToProjectGroup) {
      setSizeError("");
      return;
    }
    const projectIds = searchIndex
      .filter(
        (item) => item.type === "project" && item.projectGroupId === Number(assignedToProjectGroup),
      )
      .map((item) => item.id);
    if (projectIds.length === 0) {
      setSizeError("");
      return;
    }
    setIsComputingPgSize(true);
    setSizeError("");
    void dispatch(getProjectGroupSize({ projectsIds: projectIds.join(",") }))
      .then((res) => {
        if (getProjectGroupSize.fulfilled.match(res) && res.payload.data > 10737418240) {
          setSizeError(t("tooltip.cannotCreateProjectProjectGroupTooLarge"));
        }
      })
      .finally(() => {
        setIsComputingPgSize(false);
      });
  }, [assignedToProjectGroup, destination]);

  const projectGroupOptions = [...searchIndex]
    .filter((item) => item.type === "projectGroup")
    .sort((a, b) => {
      const aTs = a.updatedAt ? Date.parse(a.updatedAt) : 0;
      const bTs = b.updatedAt ? Date.parse(b.updatedAt) : 0;
      return bTs - aTs;
    });

  const assignedPgName =
    projectGroupOptions.find((o) => String(o.id) === assignedToProjectGroup)?.name ??
    `#${assignedToProjectGroup}`;

  const trimmedName = name.trim();
  const nameError = !trimmedName
    ? t("pages.chat.nameEmptyError")
    : trimmedName.length > 24
    ? t("pages.chat.nameTooLongError")
    : "";

  const disabledReason = isReferenceImageBusy
    ? t("pages.chat.configureGeneration.referenceImageBusyReason")
    : nameError
    ? nameError
    : destination === "assign" && !assignedToProjectGroup
    ? t("pages.chat.createProjectFromAiGraph.selectAProjectGroup")
    : isComputingPgSize
    ? t("tooltip.computingPgSize")
    : sizeError
    ? sizeError
    : "";
  const isGenerateDisabled = Boolean(disabledReason) || isGenerating;

  const accessTypeOptionLabel = (opt: AuthType) => {
    switch (opt) {
      case "classic":
        return t("pages.accessTypeTable.passwordLogin");
      case "simplified":
        return t("pages.accessTypeTable.simplifiedLogin");
      default:
        return t("pages.accessTypeTable.noLogin");
    }
  };

  const handleBack = () => {
    if (hasOverrides) {
      dispatch(openStandardModal(discardPromptOverridesModalId));
      return;
    }
    onBack();
  };

  const handleGenerate = () => {
    if (isGenerateDisabled) return;
    dispatch(openStandardModal(confirmGenerationModalId));
  };

  const launchGeneration = async () => {
    if (isGenerateDisabled) return;
    setIsGenerating(true);

    const genDestination =
      destination === "assign"
        ? { mode: "assignToProjectGroup" as const, projectGroupId: Number(assignedToProjectGroup) }
        : { mode: "newProjectGroup" as const };

    const blockade =
      generateBlockadeImages && hasOverrides
        ? { enabled: true, perScenePromptOverrides: promptOverrides }
        : { enabled: generateBlockadeImages };

    const pending = dispatch(
      generateFromPlanThunk({
        conversationId,
        ragAgentIds,
        generationOptions: {
          name: trimmedName,
          destination: genDestination,
          authType,
          blockade,
        },
      }),
    );
    onGenerationStarted();

    try {
      const response = await pending;
      if (generateFromPlanThunk.rejected.match(response)) {
        void dispatch(fetchAndUpdateSpecificConversation({ conversationId }));
        const errorMessage = typeof response.payload === "string" ? response.payload : undefined;
        dispatch(
          setAlert({
            msg: errorMessage || t("pages.chat.createProjectFromAiGraph.errorWhileGenerating"),
            type: "error",
            isOpen: true,
            showCloseButton: true,
          }),
        );
        return;
      }
      await dispatch(fetchAndUpdateSpecificConversation({ conversationId }));
      dispatch(
        setAlert({
          msg: t("pages.chat.createProjectFromAiGraph.moduleGeneratedSuccessfully"),
          type: "success",
          isOpen: true,
          customTimeoutInMs: 15000,
        }),
      );
    } catch (error) {
      console.error("Error in ConfigureGenerationPanel handleGenerate:", error);
      void dispatch(fetchAndUpdateSpecificConversation({ conversationId }));
      dispatch(
        setAlert({
          msg: t("pages.chat.createProjectFromAiGraph.errorWhileGenerating"),
          type: "error",
          isOpen: true,
          showCloseButton: true,
        }),
      );
    }
  };

  const inputClasses =
    "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500";

  return (
    <div className="bg-gray-100 p-4 flex-grow overflow-y-auto rounded-md flex flex-col space-y-4">
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="btn-alternative-outline text-sm inline-flex items-center gap-1"
          onClick={handleBack}
        >
          <MdChevronLeft size={18} className="shrink-0" />
          {t("pages.chat.configureGeneration.backToPlan")}
        </button>
        <h2 className="text-lg font-bold">{t("pages.chat.configureGeneration.title")}</h2>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white shadow-sm p-3 space-y-3">
        <div>
          <label htmlFor="configureName" className="text-sm font-medium text-gray-700">
            {t("pages.chat.createProjectFromAiGraph.enterName")}
          </label>
          <input
            id="configureName"
            type="text"
            value={name}
            onChange={(e) => {
              setIsNameEdited(true);
              setName(e.target.value);
            }}
            placeholder={t("pages.chat.createProjectFromAiGraph.enterName")}
            className={inputClasses}
            autoFocus
          />
          <p className="text-red-500 text-xs min-h-[1rem]">{nameError}</p>
        </div>

        <div>
          <p className="text-sm font-medium text-gray-700 mb-1">
            {t("pages.chat.configureGeneration.destinationLabel")}
          </p>
          {isDestinationLocked ? (
            <>
              <p className="text-sm text-gray-700">
                {t("pages.chat.configureGeneration.destinationAssignLocked")} : {assignedPgName}
              </p>
              <p className="text-red-500 text-xs min-h-[1rem]">{sizeError}</p>
            </>
          ) : (
            <>
              {(["new", "assign"] as const).map((opt) => (
                <label key={opt} className="flex items-center gap-2 mb-1 cursor-pointer">
                  <input
                    type="radio"
                    name="configureDestination"
                    value={opt}
                    checked={destination === opt}
                    onChange={() => setDestination(opt)}
                    className="h-3.5 w-3.5 cursor-pointer accent-green-600"
                  />
                  <span className="text-sm text-gray-700">
                    {opt === "new"
                      ? t("pages.chat.configureGeneration.destinationNew")
                      : t("pages.chat.configureGeneration.destinationAssign")}
                  </span>
                </label>
              ))}
              {destination === "assign" && (
                <div className="mt-2">
                  <SearchableDropdown
                    options={projectGroupOptions}
                    selectedId={assignedToProjectGroup}
                    onSelect={(selectedId) => {
                      setAssignedToProjectGroup(selectedId.toString());
                    }}
                    placeholder={t("pages.chat.createProjectFromAiGraph.selectAProjectGroup")}
                    searchPlaceholder={t("pages.chat.createProjectFromAiGraph.searchAProjectGroup")}
                    noItemFound={t("pages.chat.createProjectFromAiGraph.noCourseFound")}
                  />
                  <p className="text-red-500 text-xs min-h-[1rem]">{sizeError}</p>
                </div>
              )}
            </>
          )}
        </div>

        {destination === "new" && (
          <div>
            <p className="text-sm font-medium mb-2">{t("chooseTemplateType.accessType")}</p>
            {(["none", "simplified", "classic"] as const).map((opt) => (
              <label key={opt} className="flex items-center gap-2 mb-1 cursor-pointer">
                <input
                  type="radio"
                  name="scenariaAuthType"
                  value={opt}
                  checked={authType === opt}
                  onChange={() => setAuthType(opt)}
                  className="h-3.5 w-3.5 cursor-pointer accent-green-600"
                />
                <span className="text-sm text-gray-700">{accessTypeOptionLabel(opt)}</span>
                <InfoIconWithTooltip
                  message={t(`pages.chat.createProjectFromAiGraph.accessTypeHint.${opt}`)}
                />
              </label>
            ))}
          </div>
        )}
      </div>

      <BlockadeImagesSection
        plan={plan}
        conversationId={conversationId}
        enabled={generateBlockadeImages}
        onToggleEnabled={() => setGenerateBlockadeImages((v) => !v)}
        overrides={promptOverrides}
        onSetOverride={setPromptOverride}
        onClearOverride={clearPromptOverride}
        isReferenceImageBusy={isReferenceImageBusy}
        referenceImageRealigningTo={referenceImageRealigningTo}
        onPickReferenceImage={onPickReferenceImage}
        onRemoveReferenceImage={onRemoveReferenceImage}
      />

      <div className="pt-3 flex flex-col items-center gap-2">
        <div className="min-h-[1.25rem] flex items-center gap-1.5 text-xs text-gray-500 italic">
          <IoMdInformationCircleOutline className="shrink-0" />
          <span>
            {t("pages.chat.configureGeneration.estimate", {
              sceneCount: estimate.sceneCount,
              imageCount: estimate.imageCount,
              range:
                estimate.imageCount > 0
                  ? t("pages.chat.configureGeneration.estimateRangeWithImages")
                  : t("pages.chat.configureGeneration.estimateRangeNoImages"),
            })}
          </span>
        </div>
        <Tooltip title={disabledReason} disableHoverListener={!disabledReason}>
          <span>
            <button
              type="button"
              className="btn-primary-fill"
              disabled={isGenerateDisabled}
              onClick={handleGenerate}
            >
              {t("general.generate")}
            </button>
          </span>
        </Tooltip>
      </div>

      <ModalStandard id={discardPromptOverridesModalId} modalType="standard" className="w-[28rem]">
        <div className="p-2 space-y-4">
          <p className="text-sm text-gray-700">
            {t("pages.chat.configureGeneration.discardOverridesBody")}
          </p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="btn-alternative-outline"
              onClick={() => dispatch(closeStandardModal(discardPromptOverridesModalId))}
            >
              {t("pages.chat.configureGeneration.discardOverridesStay")}
            </button>
            <button
              type="button"
              className="btn-primary-fill"
              onClick={() => {
                dispatch(closeStandardModal(discardPromptOverridesModalId));
                onBack();
              }}
            >
              {t("pages.chat.configureGeneration.discardOverridesLeave")}
            </button>
          </div>
        </div>
      </ModalStandard>

      <ModalStandard id={confirmGenerationModalId} modalType="standard" className="w-[36rem]">
        <div className="p-3 space-y-3">
          <p className="text-base font-semibold text-gray-800">
            {t("pages.chat.configureGeneration.confirmTitle")}
          </p>
          <div className="divide-y divide-gray-100">
            <div className="pb-2.5">
              <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                {t("pages.chat.configureGeneration.destinationLabel")}
              </h4>
              <p className="text-sm text-gray-700 mt-0.5">
                {destination === "new"
                  ? t("pages.chat.configureGeneration.confirmNewCourse", {
                      name: trimmedName,
                    })
                  : t("pages.chat.configureGeneration.confirmAssignCourse", {
                      name: trimmedName,
                      pgName: assignedPgName,
                    })}
              </p>
              {destination === "new" && (
                <p className="text-sm text-gray-700 mt-0.5">
                  {t("pages.chat.configureGeneration.confirmAccessType")}{" "}
                  <span className="font-medium">{accessTypeOptionLabel(authType)}</span>
                </p>
              )}
            </div>
            <div className="py-2.5 space-y-1.5">
              <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                {t("pages.chat.configureGeneration.confirmImagesTitle")}
              </h4>
              {generateBlockadeImages ? (
                <>
                  <p className="text-sm text-gray-700">
                    {t("pages.chat.configureGeneration.confirmWithImages", {
                      count: estimate.imageCount,
                    })}
                  </p>
                  <DecorStatusRow plan={plan} contextImages={contextImages} />
                  {hasOverrides && (
                    <p className="text-sm text-gray-700">
                      {t("pages.chat.configureGeneration.confirmOverrides", {
                        count: Object.keys(promptOverrides).length,
                      })}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  {t("pages.chat.configureGeneration.confirmWithoutImages")}
                </p>
              )}
            </div>
            <div className="pt-2.5">
              <p className="text-xs text-gray-500 italic">
                {t("pages.chat.configureGeneration.confirmEndsConversation")}
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="btn-alternative-outline"
              onClick={() => dispatch(closeStandardModal(confirmGenerationModalId))}
            >
              {t("general.cancel")}
            </button>
            <button
              type="button"
              className="btn-primary-fill"
              disabled={isGenerateDisabled}
              onClick={() => {
                dispatch(closeStandardModal(confirmGenerationModalId));
                void launchGeneration();
              }}
            >
              {t("general.generate")}
            </button>
          </div>
        </div>
      </ModalStandard>
    </div>
  );
};

export default ConfigureGenerationPanel;
